import React from 'react';
import { NavLink } from 'react-router-dom';
import { type ViewType } from '../../config/permissions';

const COLLAPSE_KEY = 'fzn.sidenav.collapsed';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Match nested routes too (e.g. /explorer/AAPL should keep Explorer active) */
  matchPrefix?: boolean;
  /**
   * Tier-gated view. The row is hidden for users who lack the tier, so the nav
   * does not advertise a page that only renders a locked card. `ViewGate` still
   * guards the route itself — this is presentation, not enforcement.
   */
  viewType?: ViewType;
}

const ICON = 'w-4 h-4 flex-shrink-0';

const PAGES: NavItem[] = [
  {
    to: '/',
    label: 'Dashboards',
    icon: (
      <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    to: '/explorer',
    label: 'Ticker Explorer',
    viewType: 'ticker-explorer',
    matchPrefix: true,
    icon: (
      <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/market',
    label: 'Market Overview',
    viewType: 'market-overview',
    icon: (
      <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M3 17l5-6 4 4 5-7 4 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    to: '/calendar',
    label: 'Calendar',
    viewType: 'calendars',
    icon: (
      <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
      </svg>
    ),
  },
];

const SETTINGS: NavItem = {
  to: '/profile',
  label: 'Settings',
  matchPrefix: true,
  icon: (
    <svg className={ICON} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 3.68 1.65 1.65 0 0010 2.17V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 8v0a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
};

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={!item.matchPrefix}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `flex items-center rounded transition-colors ${
          collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-3 py-2'
        } ${
          // Active: gold on a gold-tint pill, weight 600.
          // Idle: bone at 65%, weight 500 — same family, just receded.
          isActive
            ? 'bg-accent-yellow/10 text-accent-yellow font-semibold'
            : 'text-white/65 font-medium hover:text-white hover:bg-dark-surface'
        }`
      }
    >
      {item.icon}
      {!collapsed && <span className="text-sm truncate">{item.label}</span>}
    </NavLink>
  );
}

export const SideNav: React.FC = () => {
  /*
   * Tier-gated pages are SHOWN, not hidden (2026-09-10).
   *
   * Hiding them meant a Zeroday user had no way to learn Ticker Explorer
   * exists; the ask is that they see it and meet the upgrade prompt on open.
   * `App.tsx` wraps each gated route in `ViewGate`, so the lock is enforced at
   * the destination and this rail is free to advertise it.
   *
   * `MobileTabBar` was changed to match — do not re-add entitlement filtering
   * to either nav.
   */
  const pages = PAGES;

  // Persisted so the rail state survives reloads, like the rest of the app's
  // layout preferences.
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* private mode — fall back to in-memory only */
      }
      return next;
    });
  };

  return (
    <nav
      className={`${
        collapsed ? 'w-14' : 'w-48'
      } flex-shrink-0 flex flex-col bg-chrome border-r border-dark-border transition-[width] duration-150`}
    >
      {/* Header / collapse control */}
      <div
        className={`flex items-center h-9 px-3 flex-shrink-0 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        {!collapsed && (
          <span className="text-[10px] font-bold uppercase tracking-[1.5px] text-gray-500">
            Pages
          </span>
        )}
        <button
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand' : 'Collapse'}
          className="text-gray-500 hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path
              d={collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Pages */}
      <div className={`flex-1 flex flex-col gap-0.5 ${collapsed ? 'px-2' : 'px-2'} pt-1`}>
        {pages.map((item) => (
          <NavRow key={item.to} item={item} collapsed={collapsed} />
        ))}
      </div>

      {/* Settings pinned to the bottom */}
      <div className="px-2 pb-3 pt-2 border-t border-dark-border">
        <NavRow item={SETTINGS} collapsed={collapsed} />
      </div>
    </nav>
  );
};
